import React, { memo } from 'react';
import styled, { css } from 'styled-components';

import {
  EarlyArrival,
  LateDeparture,
  Flight as FlightIcon,
} from '@lt/components/icons-react';

import { Charter as FlightIconUnknownType } from '@lt/components/icons-react/checkout-flight-ticket';

import type { FlightsData } from 'checkout/CheckoutRoutes/PackageCheckout/types/flights';
import { getHours } from 'date-fns';
import {
  FLIGHT_ARRIVAL_EARLY_TIME_WARNING_OFFSET,
  FLIGHT_LATE_DEPARTURE_TIME_WARNING_OFFSET,
  FLIGHT_TYPES,
  FLIGHT_TYPES_NAME,
} from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import {
  getDateWithoutTimezone,
  getFlightTypeData,
} from 'checkout/CheckoutRoutes/PackageCheckout/utils/flightUtils';
import { i18n } from '@lt/localization';
import LabelWithDescription from './components/LabelWithDescription';

const IconBackground = css`
  border-radius: 20px;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const IconBackgroundEarlyLate = styled.div`
  ${IconBackground}
  background: #f9eaec;
`;

/**
 * тултип для типов рейса
 */
const IconBackgroundFlight = styled.div`
  ${IconBackground}
  border: 1px solid rgb(248, 231, 28);
  outline: rgb(248, 231, 28) solid 1px;
  outline-offset: -3px;
  padding: 2px;
`;

type FlightTypeLabelProps = {
  type: string;
  text?: string;
  tooltipTitle?: string;
  flightNumber?: string;
  tooltipExtra?: string;
  flightClassText: string;
};

export const FlightTypeLabel = memo(
  ({
    type,
    text,
    tooltipTitle,
    flightNumber,
    tooltipExtra,
    flightClassText,
  }: FlightTypeLabelProps): JSX.Element => (
    <LabelWithDescription
      text={text}
      tooltipTitle={tooltipTitle}
      tooltipExtra={tooltipExtra}
      flightNumber={flightNumber}
      flightClassText={flightClassText}
      icon={
        <>
          {type === FLIGHT_TYPES_NAME.NA ? (
            <FlightIconUnknownType height={24} />
          ) : (
            <IconBackgroundFlight>
              <FlightIcon height={18} />
            </IconBackgroundFlight>
          )}
        </>
      }
    />
  ),
);

interface LabelsContainerProps {
  data: FlightsData['to'];
  type: 'to' | 'back';
  flightNumber?: string;
  isOrderPage?: boolean;
  isBusinessClass: boolean;
}

export const LabelsContainer = ({
  data,
  type,
  isOrderPage,
  flightNumber,
  isBusinessClass,
}: LabelsContainerProps): JSX.Element => {
  const { arrival, time, departure } = data;

  const parseDeparture = getDateWithoutTimezone(departure);
  const parseArrival = getDateWithoutTimezone(arrival);

  // получаем данные для лейбла раннего вылета
  const showEarlyArrivingWarning =
    type === 'to' &&
    time &&
    arrival &&
    getHours(new Date(parseArrival)) < FLIGHT_ARRIVAL_EARLY_TIME_WARNING_OFFSET;

  // получаем данные для лейбла позднего перелета
  const showLateDepartureWarning =
    type === 'back' &&
    departure &&
    getHours(new Date(parseDeparture)) >=
      FLIGHT_LATE_DEPARTURE_TIME_WARNING_OFFSET;

  // получаем данные для лебйла о типе рейса
  const { flightType } = getFlightTypeData(data);
  const flightTypeData = FLIGHT_TYPES[flightType];
  const tooltipTitle = isOrderPage ? undefined : flightTypeData.tooltipTitle;
  const tooltipDescription = isOrderPage
    ? undefined
    : flightTypeData.tooltipDescription;
  const earlyTooltipTitle = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.early_arrival.tooltip_title');
  const earlyTooltipDescription = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.early_arrival.tooltip_description');
  const lateTooltipTitle = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.late_departure.tooltip_title');
  const lateTooltipDescription = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.late_departure.tooltip_description');
  const flightClassText = isBusinessClass
    ? i18n.t('checkout.flight.business.name')
    : i18n.t('checkout.flight.economy.name');

  return (
    <Container>
      {flightType && (
        <FlightTypeLabel
          type={flightType}
          text={flightTypeData.title}
          tooltipTitle={tooltipTitle}
          tooltipExtra={tooltipDescription}
          flightNumber={flightNumber}
          flightClassText={flightClassText}
        />
      )}
      {showEarlyArrivingWarning && (
        <LabelWithDescription
          text={i18n.t('checkout.flight.early_arrival.title')}
          tooltipTitle={earlyTooltipTitle}
          tooltipExtra={earlyTooltipDescription}
          icon={
            <IconBackgroundEarlyLate>
              <EarlyArrival height={20} />
            </IconBackgroundEarlyLate>
          }
        />
      )}
      {showLateDepartureWarning && (
        <LabelWithDescription
          text={i18n.t('checkout.flight.late_departure.title')}
          tooltipTitle={lateTooltipTitle}
          tooltipExtra={lateTooltipDescription}
          icon={
            <IconBackgroundEarlyLate>
              <LateDeparture height={20} />
            </IconBackgroundEarlyLate>
          }
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  & > * {
    margin: 2px 17px 2px 0;
  }

  &:last-child {
    margin-right: 0px;
  }
`;
